/* eslint-disable react/prop-types */
/**
 * CategoriesProvider
 * Contain most logic handing with Omnitech Api
 */
import React, { useContext, useMemo, useState, useEffect, useRef } from 'react'
import _ from 'lodash'
import { CacheChecksumContext, createPersistedState } from '../../cache'
import { cancelRequest, getAllDataInParallel } from '../../helpers'
import CategoriesContext from './categories-context'
import { fetchCategories, fetchCategoryByCode } from './categories-api'

// create use state hook with persisted storage
// TODO: support using different storage provider
const useCategoriesState = createPersistedState('categories')
const useCategoriesChecksumState = createPersistedState('categoriesChecksum')

export default function CategoriesProvider({ children, config, currentLocale }) {
  // Ref
  const tempCacheRef = useRef({})

  // handle cache checksum value
  const { getChecksum } = useContext(CacheChecksumContext)
  const cacheChecksumValue = getChecksum('categories')

  // internal state
  const [categories, setCategories] = useCategoriesState([])
  const [tempCache, setTempCache] = useState([])
  const [categoriesChecksum, setCategoriesChecksum] = useCategoriesChecksumState('')
  const [categoriesReady, setCategoriesReady] = useState(false)
  const [enableRefresh, setEnableRefresh] = useState(false)

  // get cache with current locale

  const cacheChecksumValueWithCurrentLocale = useMemo(() => (
    `${currentLocale}|${getChecksum('categories') }`
  ), [currentLocale, getChecksum])

  // use reference for temp cache
  tempCacheRef.current = tempCache

  /**
   * apiFetchCategories
   * api call to fetch category
   */
  function apiFetchCategories({page = 1, pageSize = undefined } = {}) {
    return fetchCategories({
      includes: [
        'child_ids',
        'parent_id',
        'department_id',
        'seo',
      ].join(','),
      page,
      pageSize
    })
  }

  /**
   * handleFetchAll
   * handle to call all pages of categories data
   * @param {*} allCB
   * @param {*} endCB
   */
 async function handleFetchAll(allCB, endCB) {
  // if allCB is not a function, trigger end callback directly
  if (!_.isFunction(allCB)) {
    setCategories(tempCacheRef.current)
    if (_.isFunction(endCB)) endCB()
    return
  }

  try {
    const allData = await allCB()
    let data = []
    _.forEach(allData, (d) => {
      data = _.uniqBy(_.compact(_.concat(data, d.categories)), 'id')
    })
    setCategories([
      ...tempCacheRef.current,
      ...data
    ])
    if (_.isFunction(endCB)) endCB()
  } catch (error) {
    console.warn('fail to load categories for cache: ', error)
  }
}

  function fetchCategoriesAll(params) {
    return getAllDataInParallel({
      fn: (args) => fetchCategories({
        ...params,
        ...args,
      }),
      key: 'categories',
    })
  }


  /**
   * handleFetchNext
   * handle to call next page of categories data
   * @param {*} nextCB
   * @param {*} endCB
   */
  async function handleFetchNext(nextCB, endCB) {
    // if next is not a function, trigger end callback directly
    if (!_.isFunction(nextCB)) {
      setCategories(tempCacheRef.current)
      if (_.isFunction(endCB)) endCB()
      return
    }

    try {
      const { categories: data, next } = await nextCB()
      if (_.isFunction(next)) {
        setTempCache([
          ...tempCacheRef.current,
          ...data
        ])
        handleFetchNext(next, endCB)
      } else {
        // once all pages is fetched, update the actual state used
        setCategories([
          ...tempCacheRef.current,
          ...data
        ])
        if (_.isFunction(endCB)) endCB()
      }
    } catch (error) {
      console.warn('fail to load categories for cache: ', error)
    }
  }

  /**
   * handleFetchCategories
   * get categories for caching, will be used for making menu
   * @param {string} concurrency Set the concurrency of the api calls. Supported options are `parallel|sequential`. Defaults to `parallel`
   */
  async function handleFetchCategories(concurrency = 'parallel') {
    // use categories in cache first
    if (
      !_.isEmpty(categories) &&
      categoriesChecksum === cacheChecksumValueWithCurrentLocale
    ) {
      setCategoriesReady(true)
      setEnableRefresh(true)
      return
    }
    try {
      setEnableRefresh(false)
      const { cacheChecksum, categories: data, next, all } = await apiFetchCategories({ pageSize: concurrency === 'parallel' ? 100 : undefined })
      setTempCache(data)
      switch (concurrency) {
        // fetch data sequantially, i.e. request pages one at a time
        case 'sequential':
          handleFetchNext(next, () => {
            setCategoriesReady(true)
            setEnableRefresh(true)
            setCategoriesChecksum(`${currentLocale}|${cacheChecksum}`)
          })
          break
        // fetch data in parallel, i.e. request all pages at the same time
        case 'parallel':
          handleFetchAll(all, () => {
            setCategoriesReady(true)
            setEnableRefresh(true)
            setCategoriesChecksum(`${currentLocale}|${cacheChecksum}`)
          })
          break
      }
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to load categories for cache: ', error)
    }
  }

  /**
   * handleRefreshCategoriesCache
   * refresh departments cache when checksum is changed
   */
  async function handleRefreshCategoriesCache(concurrency = 'parallel') {
    // when it is not required to update cache, skip the process
    if (!enableRefresh || categoriesChecksum === cacheChecksumValueWithCurrentLocale) {
      if (_.isEmpty(categoriesChecksum) && !_.isEmpty(cacheChecksumValue)) {
        setCategoriesChecksum(cacheChecksumValueWithCurrentLocale)
      }
      return
    }

    try {
      // start refreshing cache data
      setEnableRefresh(false)
      const { cacheChecksum, categories: data, next, all } = await apiFetchCategories({ pageSize: concurrency === 'parallel' ? 100 : undefined })
      setTempCache(data)
      switch (concurrency) {
        // fetch data sequantially, i.e. request pages one at a time
        case 'sequential':
          handleFetchNext(next, () => {
            setEnableRefresh(true)
            setCategoriesChecksum(`${currentLocale}|${cacheChecksum}`)
          })
          break
        // fetch data in parallel, i.e. request all pages at the same time
        case 'parallel':
          handleFetchAll(all, () => {
            setEnableRefresh(true)
            setCategoriesChecksum(`${currentLocale}|${cacheChecksum}`)
          })
          break
        }
    } catch (error) {
      setEnableRefresh(true)
      console.warn('fail to refresh categories for cache', error)
    }
  }

  /**
   * get all categories from omnitech API when app is mounted
   */
  useEffect(() => {
    handleFetchCategories()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchCategories'])
    }
  }, [])

  /**
   * update categories cache base on cache checksum
   */
  useEffect(() => {
    if (!categoriesReady || _.isUndefined(cacheChecksumValue)) return
    handleRefreshCategoriesCache()
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchCategories'])
    }
  }, [cacheChecksumValue, cacheChecksumValueWithCurrentLocale, categoriesReady])

  const state = {
    categories,
    categoriesReady,
    fetchCategories,
    fetchCategoriesAll,
    fetchCategoryByCode,
  }

  return (
    <CategoriesContext.Provider value={state}>
      {children}
    </CategoriesContext.Provider>
  )
}
